import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './LodgeAttendancePage.css';

export function availableLodgeRegisters(surveys, lodgeId, now = new Date()) {
    return surveys.filter(s => s.kind === 'digital_attendance' && s.is_active
        && s.meeting?.lodgeId === lodgeId
        && (!s.expires_at || new Date(s.expires_at) > now)).sort((a, b) => a.meeting.date.localeCompare(b.meeting.date));
}

export default function LodgeAttendancePage() {
    const { lodgeId } = useParams();
    const navigate = useNavigate();
    const [lodges, setLodges] = useState([]);
    const [surveys, setSurveys] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState('');
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([axios.get('/api/attendance/lodges'), axios.get('/api/surveys')]).then(([l, s]) => {
            if (!cancelled) { setLodges(l.data); setSurveys(s.data); setError(''); }
        }).catch(() => { if (!cancelled) setError('Could not load lodge registers. Please refresh to try again.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [lodgeId]);
    const registers = availableLodgeRegisters(surveys, lodgeId);
    useEffect(() => {
        if (lodgeId && !loading && !error && lodges.some(l => l.id === lodgeId)) {
            navigate(`/surveys?lodge=${encodeURIComponent(lodgeId)}#meeting-registers`, { replace: true });
        }
    }, [lodgeId, loading, error, surveys, navigate]); // A fresh scan resolves against the current register list.
    const lodge = lodges.find(l => l.id === lodgeId);
    if (loading) return <main className="lodge-attendance"><p role="status">Finding attendance registers…</p></main>;
    if (error) return <main className="lodge-attendance"><p role="alert">{error}</p></main>;
    return <main className="lodge-attendance">
        <p className="lodge-qr-eyebrow">Lodge attendance</p>
        <h1>{lodgeId ? lodge?.name || 'Lodge not found' : 'Permanent lodge QR codes'}</h1>
        {lodgeId ? <>
            <p>{registers.length ? 'Choose the meeting you are attending. Check its date before opening the register.' : 'No attendance register is currently open. Please check with the Secretary.'}</p>
            {registers.map(s => <Link className="lodge-qr-card" key={s.slug} to={`/surveys?survey=${encodeURIComponent(s.slug)}&lodge=${encodeURIComponent(lodgeId)}`}><strong>{s.meeting.date}</strong><span>{s.meeting.type}</span><span>Open register →</span></Link>)}
            <Link to="/attendance">View lodge QR codes</Link>
        </> : <>
            <p>One link for each lodge, reused for every meeting. A scan opens the meeting list with your lodge selected, showing its open RSVPs and attendance registers. Meeting tokens and member login still apply.</p>
            <label htmlFor="lodge-search">Find your lodge</label>
            <input id="lodge-search" type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search lodge name…" />
            {["localhost", "127.0.0.1"].includes(window.location.hostname) && <p className="lodge-qr-note">Local trial: these QR codes use this computer’s local address. Use the live site to print QR codes for phones.</p>}
            {lodges.filter(l => l.name.toLowerCase().includes(search.toLowerCase())).map(l => {
                const url = `${window.location.origin}/attendance/${l.id}`;
                return <article className="lodge-qr-card" key={l.id}>
                    <h2>{l.name}</h2>
                    <img src={`https://quickchart.io/qr?size=240&margin=2&text=${encodeURIComponent(url)}`} alt={`Permanent attendance QR code for ${l.name}`} width="180" height="180" loading="lazy" />
                    <Link to={`/attendance/${l.id}`}>Test lodge attendance link →</Link>
                    <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(url); setCopied(l.id); } catch { setCopied('failed'); } }}>Copy permanent link</button>
                    {copied === l.id && <span role="status">Link copied.</span>}
                    <small>{url}</small>
                </article>;
            })}
            {copied === 'failed' && <p role="alert">Could not copy. Copy the link shown under the lodge instead.</p>}
        </>}
    </main>;
}
